import {
  Alert,
  Badge,
  Button,
  DateInput,
  EmptyState,
  FieldBlock,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { OrbindApiError } from '@orbind/api-client'
import type { FeatureToggleKind } from '@orbind/core'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { useBootstrap } from '../../lib/catalog.ts'
import { SwitchCard } from './catalog-shared.tsx'

/**
 * Cadastro de Feature Toggles (SPEC-20260814-1144), no padrao FormScreen
 * (SPEC-20260815-0821): o editor ocupa a area de trabalho e as excecoes por
 * workspace e por membro viram SECOES da propria edicao (aplicam na
 * hora, como o plano na tela do workspace).
 *
 * O interruptor da lista mexe no valor da INSTALACAO. Toggle do sistema nao
 * pode ser arquivado nem ter o codigo alterado (ha `requireToggle(...)` no
 * backend apontando para ele).
 */

type ToggleList = Awaited<ReturnType<Awaited<ReturnType<(typeof api)['feature-toggles']['$get']>>['json']>>
type ToggleRow = ToggleList['toggles'][number]

const KINDS: readonly { value: FeatureToggleKind; label: string; tone: 'info' | 'warning' | 'success'; icon: string }[] =
  [
    { value: 'RELEASE', label: 'Release', tone: 'info', icon: 'fas fa-rocket' },
    { value: 'OPS', label: 'Operação', tone: 'warning', icon: 'fas fa-life-ring' },
    { value: 'ENTITLEMENT', label: 'Plano', tone: 'success', icon: 'fas fa-cubes' },
  ]

const kindDe = (valor: string) => KINDS.find((k) => k.value === valor) ?? KINDS[0]!

/** "2026-08-14" -> Date local (meio-dia evita voltar um dia por fuso). */
const dataDeIso = (iso: string | null) => (iso ? new Date(`${iso}T12:00:00`) : null)
const isoDeData = (data: Date | null) =>
  data
    ? `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`
    : null
const dataCurta = (iso: string | null) => {
  const data = dataDeIso(iso)
  return data ? data.toLocaleDateString('pt-BR') : null
}

/** "correção rápida" -> "CORRECAO_RAPIDA". */
const normalizarCodigo = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase()

/** Mesmo interruptor acessivel da pagina do admin (role=switch + rotulo). */
function Interruptor({
  ligado,
  rotulo,
  titulo,
  aoAlternar,
}: {
  ligado: boolean
  rotulo: string
  titulo: string
  aoAlternar: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      title={titulo}
      className={`adm-switch${ligado ? ' adm-switch--on' : ''}`}
      onClick={aoAlternar}
    >
      <span className="adm-switch__knob" aria-hidden />
    </button>
  )
}

type FormToggle = {
  code: string
  kind: FeatureToggleKind
  description: string
  enabled: boolean
  ownerMemberId: string | null
  removalTargetDate: Date | null
}

const FORM_VAZIO: FormToggle = {
  code: '',
  kind: 'RELEASE',
  description: '',
  enabled: false,
  ownerMemberId: null,
  removalTargetDate: null,
}

export function FeatureTogglesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()
  const bootstrap = useBootstrap()

  const lista = useQuery({
    queryKey: ['feature-toggles'],
    queryFn: async () => (await api['feature-toggles'].$get()).json(),
  })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormToggle>(FORM_VAZIO)
  const [confirmandoArquivo, setConfirmandoArquivo] = useState<string | null>(null)

  // todos os workspaces da instalacao — para a excecao por workspace
  const workspaces = useQuery({
    queryKey: ['plan-workspaces'],
    queryFn: async () => (await api.plans.workspaces.$get()).json(),
  })

  const aoFalhar = (erro: unknown) => {
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })
  }

  /** Invalida lista E bundle: alternar um toggle muda o que o app inteiro ve. */
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['feature-toggles'] })
    void qc.invalidateQueries({ queryKey: ['feature-toggle-bundle'] })
  }

  const criar = useMutation({
    mutationFn: async (corpo: Record<string, unknown>) =>
      (await api['feature-toggles'].$post({ json: corpo as never })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Toggle criado.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      (await api['feature-toggles'][':id'].$patch({ param: { id }, json: corpo as never })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Toggle atualizado.' })
    },
    onError: aoFalhar,
  })

  const alternar = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      (await api['feature-toggles'][':id'].enabled.$post({ param: { id }, json: { enabled } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const arquivar = useMutation({
    mutationFn: async (id: string) => (await api['feature-toggles'][':id'].archive.$post({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoArquivo(null)
      toast.push({ tone: 'success', message: 'Toggle arquivado.' })
    },
    onError: (erro) => {
      setConfirmandoArquivo(null)
      aoFalhar(erro)
    },
  })

  const gravarWsOverride = useMutation({
    mutationFn: async ({
      id,
      corpo,
    }: {
      id: string
      corpo: { workspaceId: string; enabled: boolean; reason: string | null }
    }) => (await api['feature-toggles'][':id']['workspace-overrides'].$put({ param: { id }, json: corpo })).json(),
    onSuccess: () => {
      invalidar()
      toast.push({ tone: 'success', message: 'Exceção de workspace gravada.' })
    },
    onError: aoFalhar,
  })

  const removerWsOverride = useMutation({
    mutationFn: async ({ id, overrideId }: { id: string; overrideId: string }) =>
      (
        await api['feature-toggles'][':id']['workspace-overrides'][':overrideId'].$delete({
          param: { id, overrideId },
        })
      ).json(),
    onSuccess: () => {
      invalidar()
      toast.push({ tone: 'success', message: 'Exceção de workspace removida.' })
    },
    onError: aoFalhar,
  })

  const gravarOverride = useMutation({
    mutationFn: async ({ id, corpo }: { id: string; corpo: { memberId: string; enabled: boolean; reason: string | null } }) =>
      (await api['feature-toggles'][':id'].overrides.$put({ param: { id }, json: corpo })).json(),
    onSuccess: () => {
      invalidar()
      toast.push({ tone: 'success', message: 'Exceção gravada.' })
    },
    onError: aoFalhar,
  })

  const removerOverride = useMutation({
    mutationFn: async ({ id, overrideId }: { id: string; overrideId: string }) =>
      (
        await api['feature-toggles'][':id'].overrides[':overrideId'].$delete({ param: { id, overrideId } })
      ).json(),
    onSuccess: () => {
      invalidar()
      toast.push({ tone: 'success', message: 'Exceção removida.' })
    },
    onError: aoFalhar,
  })

  const toggles = lista.data?.toggles ?? []
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return toggles
    return toggles.filter((t) => `${t.code} ${t.description ?? ''}`.toLowerCase().includes(termo))
  }, [toggles, busca])

  const membros = bootstrap.data?.members ?? []
  const nomeDoDono = (id: string | null) => membros.find((c) => c.id === id)?.name ?? null

  const abrirEditor = (alvo: ToggleRow | null) => {
    setForm(
      alvo
        ? {
            code: alvo.code,
            kind: kindDe(alvo.kind).value,
            description: alvo.description ?? '',
            enabled: alvo.enabled,
            ownerMemberId: alvo.ownerMemberId,
            removalTargetDate: dataDeIso(alvo.removalTargetDate),
          }
        : FORM_VAZIO,
    )
    setEditando({ id: alvo?.id ?? null })
  }

  // "+" do explorer (AdminPage) pede a inclusao daqui
  useEffect(() => {
    if (criarSinal > 0) {
      setForm(FORM_VAZIO)
      setEditando({ id: null })
    }
  }, [criarSinal])

  const toggleEmEdicao = editando?.id ? toggles.find((t) => t.id === editando.id) : undefined

  /** Dirty check contra o toggle carregado, normalizado como o PATCH envia. */
  const normalizado = (f: FormToggle) => ({
    code: normalizarCodigo(f.code),
    kind: f.kind,
    description: f.description.trim(),
    enabled: f.enabled,
    ownerMemberId: f.ownerMemberId,
    removalTargetDate: isoDeData(f.removalTargetDate),
  })
  const alterado =
    editando?.id == null
      ? normalizarCodigo(form.code).length >= 2
      : toggleEmEdicao !== undefined &&
        JSON.stringify(normalizado(form)) !==
          JSON.stringify(
            normalizado({
              code: toggleEmEdicao.code,
              kind: kindDe(toggleEmEdicao.kind).value,
              description: toggleEmEdicao.description ?? '',
              enabled: toggleEmEdicao.enabled,
              ownerMemberId: toggleEmEdicao.ownerMemberId,
              removalTargetDate: dataDeIso(toggleEmEdicao.removalTargetDate),
            }),
          )

  // ------------------------------------------- formulário (FormScreen) ---

  if (editando !== null) {
    const salvarValido =
      alterado && normalizarCodigo(form.code).length >= 2 && !criar.isPending && !atualizar.isPending
    return (
      <FormScreen
        title={editando.id ? 'Editar feature toggle' : 'Novo feature toggle'}
        subtitle={editando.id ? toggleEmEdicao?.code : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o código para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar toggle'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          const corpo = {
            code: normalizarCodigo(form.code),
            kind: form.kind,
            description: form.description.trim() === '' ? null : form.description.trim(),
            enabled: form.enabled,
            ownerMemberId: form.ownerMemberId,
            removalTargetDate: isoDeData(form.removalTargetDate),
          }
          if (editando.id) atualizar.mutate({ id: editando.id, corpo })
          else criar.mutate(corpo)
        }}
      >
        <FormSection title="Informações do toggle">
          <FormRow cols="1fr 1fr 1fr">
            <FieldBlock
              label="Código técnico"
              required
              hint="SCREAMING_SNAKE_CASE — é a chave que o código-fonte consulta. Imutável depois de usada."
            >
              {({ id, invalid }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  autoFocus
                  value={form.code}
                  disabled={Boolean(toggleEmEdicao?.isSystem)}
                  placeholder="Ex.: NOVO_QUADRO_KANBAN"
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, code: normalizarCodigo(f.code) }))}
                />
              )}
            </FieldBlock>
            <FieldBlock
              label="Natureza"
              hint="Release esconde o que está em construção; Operação é kill-switch; Plano liga módulos contratados."
            >
              {({ id }) => (
                <Select<FeatureToggleKind>
                  id={id}
                  value={form.kind}
                  options={KINDS.map((k) => ({ value: k.value, label: k.label, icon: k.icon }))}
                  onChange={(kind) => setForm((f) => ({ ...f, kind }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Dono" hint="Quem responde pelo toggle — e pela remoção, quando for de release.">
              {({ id }) => (
                <Select<string>
                  id={id}
                  value={form.ownerMemberId ?? 'SEM_DONO'}
                  options={[
                    { value: 'SEM_DONO', label: 'Sem dono definido' },
                    ...membros.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                  onChange={(valor) => setForm((f) => ({ ...f, ownerMemberId: valor === 'SEM_DONO' ? null : valor }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="2fr 1fr">
            <FieldBlock label="Descrição">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.description}
                  placeholder="O que este toggle liga/desliga e por quê"
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock
              label="Data-alvo de remoção"
              hint="Toggle de release que sobrevive ao release é dívida técnica."
            >
              {({ id }) => (
                <DateInput
                  id={id}
                  value={form.removalTargetDate}
                  onChange={(data) => setForm((f) => ({ ...f, removalTargetDate: data }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="minmax(220px, 420px)">
            <SwitchCard
              title={form.enabled ? 'Ligado nesta instalação' : 'Desligado nesta instalação'}
              description="É o valor padrão para todo mundo — exceções por workspace e membro vencem."
              checked={form.enabled}
              onChange={(enabled) => setForm((f) => ({ ...f, enabled }))}
            />
          </FormRow>
        </FormSection>

        {toggleEmEdicao ? (
          <>
            <FormSection
              title={`Exceções por workspace${toggleEmEdicao.workspaceOverrides.length > 0 ? ` (${toggleEmEdicao.workspaceOverrides.length})` : ''}`}
              defaultOpen={toggleEmEdicao.workspaceOverrides.length > 0}
            >
              <WorkspaceOverridesEditor
                toggle={toggleEmEdicao}
                workspaces={workspaces.data?.workspaces ?? []}
                gravando={gravarWsOverride.isPending}
                onGravar={(corpo) => gravarWsOverride.mutate({ id: toggleEmEdicao.id, corpo })}
                onRemover={(overrideId) => removerWsOverride.mutate({ id: toggleEmEdicao.id, overrideId })}
              />
            </FormSection>

            <FormSection
              title={`Exceções por membro${toggleEmEdicao.overrides.length > 0 ? ` (${toggleEmEdicao.overrides.length})` : ''}`}
              defaultOpen={toggleEmEdicao.overrides.length > 0}
            >
              <OverridesEditor
                toggle={toggleEmEdicao}
                membros={membros}
                gravando={gravarOverride.isPending}
                onGravar={(corpo) => gravarOverride.mutate({ id: toggleEmEdicao.id, corpo })}
                onRemover={(overrideId) => removerOverride.mutate({ id: toggleEmEdicao.id, overrideId })}
              />
            </FormSection>
          </>
        ) : null}
      </FormScreen>
    )
  }

  // ------------------------------------------------------------- lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Feature toggles</h1>
        <p className="adm-head__desc">
          Ligue e desligue funcionalidades em tempo real, sem publicar uma nova versão — com exceções por workspace e
          por membro.
        </p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) {
    return (
      <>
        {cabecalho}
        <LoadingState variant="list" label="Carregando os feature toggles" />
      </>
    )
  }

  if (lista.isError) {
    // 403 e "sem permissao" — diferente de erro de rede/servidor
    const semPermissao = lista.error instanceof OrbindApiError && lista.error.status === 403
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          {semPermissao ? (
            <EmptyState
              icon="fas fa-lock"
              title="Sem permissão"
              description="Seu usuário não tem acesso à gestão de feature toggles. Fale com um administrador da instalação."
            />
          ) : (
            <Alert
              tone="danger"
              title="Não foi possível carregar os feature toggles"
              action={
                <Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>
                  Tentar de novo
                </Button>
              }
            >
              Verifique a conexão e tente novamente.
            </Alert>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input
            type="search"
            placeholder="Filtrar por código ou descrição…"
            aria-label="Filtrar feature toggles"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
          />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {toggles.length} {toggles.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState
            icon="fas fa-toggle-on"
            title={toggles.length === 0 ? 'Nenhum feature toggle ainda' : 'Nenhum toggle com esse filtro'}
            description={
              toggles.length === 0
                ? 'Crie o primeiro toggle para liberar uma funcionalidade aos poucos, desligar algo problemático sem deploy ou ligar módulos por plano.'
                : `Nada casa com “${busca}”. Tente o código técnico ou parte da descrição.`
            }
            action={
              toggles.length === 0 ? (
                <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
                  Novo feature toggle
                </Button>
              ) : (
                <Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>
                  Limpar o filtro
                </Button>
              )
            }
          />
        </div>
      ) : (
        <div className="adm-list">
          {filtrados.map((row) => {
            const kind = kindDe(row.kind)
            const dono = nomeDoDono(row.ownerMemberId)
            const remocao = dataCurta(row.removalTargetDate)
            const excecoes = row.overrides.length + row.workspaceOverrides.length
            const confirmando = confirmandoArquivo === row.id
            const legenda = [
              row.description,
              dono ? `Dono: ${dono}` : null,
              remocao ? `Remover até ${remocao}` : null,
              excecoes > 0 ? `${excecoes} ${excecoes === 1 ? 'exceção' : 'exceções'}` : null,
            ]
              .filter(Boolean)
              .join(' · ')

            return (
              <div key={row.id} className={`adm-catrow${row.enabled ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon">
                  <i className={row.enabled ? 'fas fa-toggle-on' : 'fas fa-toggle-off'} aria-hidden />
                </span>

                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.code}</span>
                  <span className="adm-catrow__sub">{legenda === '' ? 'Sem descrição' : legenda}</span>
                </span>

                <Badge tone={kind.tone} icon={kind.icon}>
                  {kind.label}
                </Badge>
                {row.isSystem ? <span className="adm-sys">Sistema</span> : null}

                {confirmando ? (
                  // confirmacao INLINE — sem modal, como o usuario prefere
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>
                      Arquivar “{row.code}”? Vale como desligado para todos.
                    </span>
                    <Button variant="danger" loading={arquivar.isPending} onClick={() => arquivar.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoArquivo(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.enabled ? 'Ligado' : 'Desligado'}</span>
                      <Interruptor
                        ligado={row.enabled}
                        rotulo={`${row.code} está ligado na instalação`}
                        titulo={row.enabled ? 'Ligado — clique para desligar' : 'Desligado — clique para ligar'}
                        aoAlternar={() => alternar.mutate({ id: row.id, enabled: !row.enabled })}
                      />
                    </span>

                    <button
                      type="button"
                      className="adm-iconbtn"
                      aria-label={`Editar ${row.code}`}
                      title="Editar (inclui as exceções)"
                      onClick={() => abrirEditor(row)}
                    >
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="adm-iconbtn adm-iconbtn--danger"
                      disabled={row.isSystem}
                      aria-label={
                        row.isSystem ? `${row.code} é do sistema e não pode ser arquivado` : `Arquivar ${row.code}`
                      }
                      title={
                        row.isSystem
                          ? 'Toggle do sistema: há código que depende dele — desligue-o se necessário'
                          : 'Arquivar (sai do bundle, mantém o histórico)'
                      }
                      onClick={() => setConfirmandoArquivo(row.id)}
                    >
                      <i className="fas fa-box-archive" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

/**
 * Excecoes por workspace: vencem o plano do workspace, perdem para a excecao
 * de membro e para o master desligado da instalacao. Aplicam NA HORA
 * (nao passam pelo Salvar do formulario).
 */
function WorkspaceOverridesEditor({
  toggle,
  workspaces,
  gravando,
  onGravar,
  onRemover,
}: {
  toggle: ToggleRow
  workspaces: readonly { id: string; name: string; slug: string }[]
  gravando: boolean
  onGravar: (corpo: { workspaceId: string; enabled: boolean; reason: string | null }) => void
  onRemover: (overrideId: string) => void
}) {
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const [valor, setValor] = useState<'on' | 'off'>(toggle.enabled ? 'off' : 'on')
  const [motivo, setMotivo] = useState('')

  const disponiveis = workspaces.filter((w) => !toggle.workspaceOverrides.some((o) => o.workspaceId === w.id))

  return (
    <>
      <p className="adm-hint">
        A exceção vale para o workspace inteiro e aplica na hora: vence o plano contratado, mas perde para exceções por
        membro.
      </p>

      {toggle.workspaceOverrides.length === 0 ? (
        <p className="adm-hint">Nenhuma exceção de workspace — vale o plano (ou o valor da instalação).</p>
      ) : (
        <div className="adm-list">
          {toggle.workspaceOverrides.map((o) => (
            <div key={o.id} className="adm-catrow">
              <span className="adm-catrow__main">
                <span className="adm-catrow__name">{o.workspaceName}</span>
                <span className="adm-catrow__sub">{o.reason ?? o.workspaceSlug}</span>
              </span>
              <Badge tone={o.enabled ? 'success' : 'neutral'} icon={o.enabled ? 'fas fa-toggle-on' : 'fas fa-toggle-off'}>
                {o.enabled ? 'Ligado' : 'Desligado'}
              </Badge>
              <button
                type="button"
                className="adm-iconbtn adm-iconbtn--danger"
                aria-label={`Remover exceção de ${o.workspaceName}`}
                title="Remover exceção"
                onClick={() => onRemover(o.id)}
              >
                <i className="fas fa-trash-alt" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      <FormRow cols="1fr 1fr 2fr">
        <FieldBlock label="Workspace">
          {({ id }) => (
            <Select<string>
              id={id}
              value={workspaceId}
              options={disponiveis.map((w) => ({ value: w.id, label: w.name }))}
              placeholder={disponiveis.length === 0 ? 'Todos já têm exceção' : 'Escolha um workspace'}
              disabled={disponiveis.length === 0}
              onChange={setWorkspaceId}
            />
          )}
        </FieldBlock>
        <FieldBlock label="O toggle fica">
          {({ id }) => (
            <Select<'on' | 'off'>
              id={id}
              value={valor}
              options={[
                { value: 'on', label: 'Ligado', icon: 'fas fa-toggle-on' },
                { value: 'off', label: 'Desligado', icon: 'fas fa-toggle-off' },
              ]}
              onChange={setValor}
            />
          )}
        </FieldBlock>
        <FieldBlock label="Motivo" hint="Ex.: módulo negociado fora do plano padrão.">
          {({ id }) => (
            <Input
              id={id}
              value={motivo}
              placeholder="Por que este workspace é exceção"
              onChange={(e) => setMotivo(e.target.value)}
            />
          )}
        </FieldBlock>
      </FormRow>

      <div>
        <Button
          variant="secondary"
          icon="fas fa-plus"
          disabled={workspaceId === null || gravando}
          onClick={() => {
            if (workspaceId === null) return
            onGravar({ workspaceId, enabled: valor === 'on', reason: motivo.trim() === '' ? null : motivo.trim() })
            setWorkspaceId(null)
            setMotivo('')
          }}
        >
          Incluir exceção
        </Button>
      </div>
    </>
  )
}

/** Excecoes por membro de um toggle: lista + inclusao. Aplicam na hora. */
function OverridesEditor({
  toggle,
  membros,
  gravando,
  onGravar,
  onRemover,
}: {
  toggle: ToggleRow
  membros: readonly { id: string; name: string; email: string }[]
  gravando: boolean
  onGravar: (corpo: { memberId: string; enabled: boolean; reason: string | null }) => void
  onRemover: (overrideId: string) => void
}) {
  const [membroId, setMembroId] = useState<string | null>(null)
  const [valor, setValor] = useState<'on' | 'off'>(toggle.enabled ? 'off' : 'on')
  const [motivo, setMotivo] = useState('')

  // quem ja tem excecao sai do select — o PUT e um upsert, mas a UI guia
  const disponiveis = membros.filter((c) => !toggle.overrides.some((o) => o.memberId === c.id))

  return (
    <>
      <p className="adm-hint">
        O valor da instalação é <strong>{toggle.enabled ? 'ligado' : 'desligado'}</strong>. A exceção vale só para o
        membro escolhido, aplica na hora e vence todas as outras camadas.
      </p>

      {toggle.overrides.length === 0 ? (
        <p className="adm-hint">Nenhuma exceção ainda — o valor da instalação vale para todo mundo.</p>
      ) : (
        <div className="adm-list">
          {toggle.overrides.map((o) => (
            <div key={o.id} className="adm-catrow">
              <span className="adm-catrow__main">
                <span className="adm-catrow__name">{o.memberName}</span>
                <span className="adm-catrow__sub">{o.reason ?? o.memberEmail}</span>
              </span>
              <Badge tone={o.enabled ? 'success' : 'neutral'} icon={o.enabled ? 'fas fa-toggle-on' : 'fas fa-toggle-off'}>
                {o.enabled ? 'Ligado' : 'Desligado'}
              </Badge>
              <button
                type="button"
                className="adm-iconbtn adm-iconbtn--danger"
                aria-label={`Remover exceção de ${o.memberName}`}
                title="Remover exceção"
                onClick={() => onRemover(o.id)}
              >
                <i className="fas fa-trash-alt" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      <FormRow cols="1fr 1fr 2fr">
        <FieldBlock label="Membro">
          {({ id }) => (
            <Select<string>
              id={id}
              value={membroId}
              options={disponiveis.map((c) => ({ value: c.id, label: c.name }))}
              placeholder={disponiveis.length === 0 ? 'Todos já têm exceção' : 'Escolha um membro'}
              disabled={disponiveis.length === 0}
              onChange={setMembroId}
            />
          )}
        </FieldBlock>
        <FieldBlock label="O toggle fica">
          {({ id }) => (
            <Select<'on' | 'off'>
              id={id}
              value={valor}
              options={[
                { value: 'on', label: 'Ligado', icon: 'fas fa-toggle-on' },
                { value: 'off', label: 'Desligado', icon: 'fas fa-toggle-off' },
              ]}
              onChange={setValor}
            />
          )}
        </FieldBlock>
        <FieldBlock label="Motivo" hint="Por que a exceção existe — quem ler daqui a meses agradece.">
          {({ id }) => (
            <Input
              id={id}
              value={motivo}
              placeholder="Ex.: piloto do novo quadro com a equipe de Produto"
              onChange={(e) => setMotivo(e.target.value)}
            />
          )}
        </FieldBlock>
      </FormRow>

      <div>
        <Button
          variant="secondary"
          icon="fas fa-plus"
          disabled={membroId === null || gravando}
          onClick={() => {
            if (membroId === null) return
            onGravar({
              memberId: membroId,
              enabled: valor === 'on',
              reason: motivo.trim() === '' ? null : motivo.trim(),
            })
            setMembroId(null)
            setMotivo('')
          }}
        >
          Incluir exceção
        </Button>
      </div>
    </>
  )
}
